import React from "react";
import {
  Box,
  TextField,
  Typography,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { CREATE_CONTACT } from "../../graph/contacts/mutations";
import useGraphQLClient from "../../hooks/useGraphQLClient";

const painPointOptions = [
  { label: "Knowledge Risk", value: "KNOWLEDGE_RISK" },
  { label: "Single Point Failure", value: "SINGLE_POINT_OF_FAILURE" },
  { label: "Staff Dependency", value: "STAFF_DEPENDENCY" },
  { label: "Training / Onboarding", value: "TRAINING_ONBOARDING" },
  { label: "Process Consistency", value: "PROCESS_CONSISTENCY" },
  { label: "Visibility", value: "OPERATIONAL_VISIBILITY" },
  { label: "Growth / Scaling", value: "GROWTH_SCALING" },
  { label: "Other", value: "OTHER" },
];

const nextStepOptions = [
  { label: "LinkedIn", value: "LINKEDIN_CONNECTION" },
  { label: "Send Info", value: "SEND_INFORMATION" },
  { label: "Follow-up Call", value: "FOLLOW_UP_CALL" },
  { label: "Coffee", value: "COFFEE_MEETING" },
  { label: "Business Audit", value: "BUSINESS_AUDIT" },
  { label: "Demo", value: "DEMONSTRATION" },
];

const EventContactCapture = () => {
  const navigate = useNavigate();
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const input = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email || null,
        phone: values.phone || null,
        jobTitle: values.jobTitle || null,
        companyName: values.companyName || null,

        source: "FSB_CONFERENCE_2026",
        status: "MET",
        engagementLevel: "LIGHTLY_ENGAGED",

        preferredCommsMethod: values.preferredCommsMethod || null,
        fitScore: values.fitScore === "" ? null : Number(values.fitScore),

        painPoint: values.painPoint || null,
        nextStep: values.nextStep || null,
        notes: values.notes || null,
      };

      await client.request(CREATE_CONTACT, { input });

      setSnackbar({
        open: true,
        message: "Contact captured",
        severity: "success",
      });

      resetForm();
    } catch (err) {
      const message = err?.response?.errors?.[0]?.message || "Unexpected error";

      setSnackbar({
        open: true,
        message: `Failed to capture contact: ${message}`,
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ pb: 10 }}>
      <Header
        title="EVENT CAPTURE"
        subtitle="FSB North Wales Small Business Conference 2026"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Paper sx={{ p: 2, borderRadius: "12px" }}>
              <Box display="grid" gap={2}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="First Name"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Last Name"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Company"
                  name="companyName"
                  value={values.companyName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Job Title"
                  name="jobTitle"
                  value={values.jobTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Phone"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Box>
                  <Typography variant="subtitle2" mb={1}>
                    Pain Point
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {painPointOptions.map((option) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        clickable
                        color={
                          values.painPoint === option.value
                            ? "secondary"
                            : "default"
                        }
                        variant={
                          values.painPoint === option.value
                            ? "filled"
                            : "outlined"
                        }
                        onClick={() => setFieldValue("painPoint", option.value)}
                      />
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" mb={1}>
                    Next Step
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {nextStepOptions.map((option) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        clickable
                        color={
                          values.nextStep === option.value
                            ? "secondary"
                            : "default"
                        }
                        variant={
                          values.nextStep === option.value
                            ? "filled"
                            : "outlined"
                        }
                        onClick={() => setFieldValue("nextStep", option.value)}
                      />
                    ))}
                  </Stack>
                </Box>

                <TextField
                  fullWidth
                  variant="filled"
                  label="Conversation Notes"
                  name="notes"
                  multiline
                  minRows={3}
                  value={values.notes}
                  onChange={handleChange}
                  placeholder="What did they say? What problem stood out?"
                />

                <ActionButton
                  type="submit"
                  tone="success"
                  size="large"
                  fullWidth
                >
                  Save & Capture Next
                </ActionButton>

                <ActionButton
                  tone="teal"
                  variant="text"
                  onClick={() => navigate("/manageContacts")}
                  fullWidth
                >
                  Finish
                </ActionButton>
              </Box>
            </Paper>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const validationSchema = yup.object().shape({
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  email: yup.string().email("Invalid email").nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  jobTitle: yup.string().nullable().notRequired(),
  companyName: yup.string().nullable().notRequired(),
  painPoint: yup.string().nullable().notRequired(),
  nextStep: yup.string().nullable().notRequired(),
  notes: yup.string().nullable().notRequired(),
});

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
  companyName: "",
  preferredCommsMethod: "",
  fitScore: "",
  painPoint: "",
  nextStep: "",
  notes: "",
};

export default EventContactCapture;